import {
  ORGANIZATION_UNIT_TYPES,
  orderUnitsAsTree,
  treeIndent,
  type OrganizationUnitType,
} from '../shared';

/**
 * Turning the units of a tenant into something a picker can show.
 *
 * Alphabetical was unusable in a real organigram: the root, the company
 * beneath it and the profit centre beneath that are all called
 * "My Organization", so the list read as three identical lines of which one
 * led to the contracts. Indented and named by type they are three places.
 */

/** Which text names each type. Built once, used by every picker. */
export const OE_TYPE_LABEL_KEYS: Record<OrganizationUnitType, string> = {
  organization: 'organigram.oeType.organization',
  company: 'organigram.oeType.company',
  profit_center: 'organigram.oeType.profitCenter',
  department: 'organigram.oeType.department',
};

/**
 * The text key for a type.
 *
 * Falls back to the raw value for a type the language has not caught up
 * with — the same reasoning as everywhere else here: an untranslated label
 * still says more than an empty one.
 */
export function oeTypeLabelKey(type: string): string {
  return (ORGANIZATION_UNIT_TYPES as readonly string[]).includes(type)
    ? OE_TYPE_LABEL_KEYS[type as OrganizationUnitType]
    : type;
}

/** A unit as a picker needs it. */
export interface UnitForPicker {
  id: string;
  name: string;
  parentId: string | null;
  type: string;
}

export interface UnitPickerOption {
  id: string;
  label: string;
  /** Kept beside the label so a richer control can indent properly later. */
  depth: number;
}

/**
 * The units as indented options, parents before children.
 *
 * `translate` is passed in rather than injected so this stays a plain
 * function: what it produces is a list of strings, and a list of strings is
 * worth checking without a TestBed.
 */
export function unitPickerOptions(
  units: readonly UnitForPicker[],
  translate: (key: string) => string,
  /**
   * How many records hang off each unit, when the caller knows.
   *
   * Appended as a count so a reader can see at a glance which unit leads
   * anywhere — in a real tree most of them hold nothing, and the picker
   * otherwise offers a dozen equally plausible dead ends.
   */
  counts?: ReadonlyMap<string, number>,
): UnitPickerOption[] {
  return orderUnitsAsTree(units).map(({ unit, depth }) => {
    const count = counts?.get(unit.id) ?? 0;
    const suffix = count > 0 ? ` (${count})` : '';
    return {
      id: unit.id,
      depth,
      label: `${treeIndent(depth)}${unit.name} · ${translate(oeTypeLabelKey(unit.type))}${suffix}`,
    };
  });
}

/**
 * The unit a picker should start on: the first in tree order that holds
 * something, else the first of all.
 *
 * Without this the preselection lands on whatever sorts first, which in an
 * organigram is the root — and a root holds no contracts, so the screen
 * opens empty and looks broken.
 */
export function firstUnitWithRecords(
  units: readonly UnitForPicker[],
  counts: ReadonlyMap<string, number>,
): string | null {
  const ordered = orderUnitsAsTree(units);
  const withRecords = ordered.find(
    ({ unit }) => (counts.get(unit.id) ?? 0) > 0,
  );
  return (withRecords ?? ordered[0])?.unit.id ?? null;
}
